import React from "react";
import {
    CalendarLtr20Regular,
    CheckmarkCircle20Filled,
    DismissCircle20Filled,
    QuestionCircle20Filled,
} from "@fluentui/react-icons";
import {
    graphMailService,
    type EinladungsAntwort,
    type TerminEinladung,
} from "../../../services/graphMailService";
import { calendarService } from "../../../services/calendarService";
import { toGraphDate } from "../../../utils/graphDatum";
import BottomSheet from "../../calendar/BottomSheet";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { beantwortbar, einladungsFehlerText, mussSenden, terminZeitText } from "../terminEinladung";
import {
    antwortKurz,
    konflikte,
    konfliktText,
    tagesAusschnitt,
    terminIntervall,
    terminKurzText,
    type KalenderTermin,
} from "./terminKurz";
import "./mailMobil.css";

type Daten = { einladung: TerminEinladung | null; darfAntworten: boolean };

/*
 * Einladungen werden in der LISTE je Zeile geladen — gemerkt und höchstens
 * zwei gleichzeitig (Exchange begrenzt die Verbindungen je Postfach).
 */
const cache = new Map<string, Promise<Daten>>();
let laufend = 0;
const warteschlange: (() => void)[] = [];
function gedrosselt<T>(lauf: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const start = () => {
            laufend += 1;
            lauf()
                .then(resolve, reject)
                .finally(() => {
                    laufend -= 1;
                    warteschlange.shift()?.();
                });
        };
        if (laufend < 2) start();
        else warteschlange.push(start);
    });
}
function ladeEinladung(mailbox: string, messageId: string): Promise<Daten> {
    const key = `${mailbox.toLowerCase()}|${messageId}`;
    let p = cache.get(key);
    if (!p) {
        p = gedrosselt(() => graphMailService.fetchEinladung(mailbox, messageId)).catch(() => {
            cache.delete(key);
            return { einladung: null, darfAntworten: false };
        });
        cache.set(key, p);
    }
    return p;
}

/** Eigene Termine am Tag der Einladung — `null`, wenn der Kalender nicht lesbar ist. */
function ladeTag(mailbox: string, e: TerminEinladung): Promise<KalenderTermin[] | null> {
    const iv = terminIntervall(e);
    if (!iv) return Promise.resolve(null);
    const von = new Date(iv.start.getFullYear(), iv.start.getMonth(), iv.start.getDate());
    const bis = new Date(von.getTime() + 86_400_000);
    return calendarService.listEvents(mailbox, von, bis).then(
        (l) => l as KalenderTermin[],
        () => null,
    );
}

export interface TerminKarteProps {
    mailbox: string;
    messageId: string;
    /** „zeile" = unter der Mail in der Liste; „karte" = unter dem Absender der geöffneten Mail. */
    variante: "zeile" | "karte";
    /** Nach einer gesendeten Antwort: Einladung löschen (wie seit #2385). */
    onBeantwortet?: () => Promise<void> | void;
}

/**
 * Termin-Einladung auf dem Handy wie Outlook iOS: kompakt „Heute, 11:30
 * (30 Min.)", in der geöffneten Mail mit Konflikten; der Knopf öffnet das
 * Antwort-Sheet (Ja / Vielleicht / Nein, E-Mail an Organisator, Nachricht).
 */
export const TerminKarte: React.FC<TerminKarteProps> = ({ mailbox, messageId, variante, onBeantwortet }) => {
    const [daten, setDaten] = React.useState<Daten | null>(null);
    const [tag, setTag] = React.useState<KalenderTermin[] | null | undefined>(undefined);
    const [offen, setOffen] = React.useState(false);

    React.useEffect(() => {
        let aktiv = true;
        setDaten(null);
        void ladeEinladung(mailbox, messageId).then((d) => aktiv && setDaten(d));
        return () => {
            aktiv = false;
        };
    }, [mailbox, messageId]);

    const e = daten?.einladung ?? null;
    // Konflikte nur in der geöffneten Mail (oder sobald das Sheet aufgeht) — in
    // der Liste wären es zu viele Kalenderabrufe.
    const brauchtTag = !!e && (variante === "karte" || offen);
    React.useEffect(() => {
        if (!brauchtTag || !e || tag !== undefined) return;
        let aktiv = true;
        void ladeTag(mailbox, e).then((l) => aktiv && setTag(l));
        return () => {
            aktiv = false;
        };
    }, [brauchtTag, e, mailbox, tag]);

    if (!e) return null;
    const kann = beantwortbar(e) && !!daten?.darfAntworten;
    const stand = e.abgesagt ? "Abgesagt" : antwortKurz(e.status);
    const anzahlKonflikte = tag ? konflikte(e, tag).length : null;
    const zeit = terminKurzText(e);
    const ort = variante === "karte" ? e.ort?.trim() : "";
    // Ohne Termindaten, Stand und Antwortmöglichkeit gäbe es nur einen leeren Kasten.
    if (!zeit && !ort && !kann && !stand) return null;

    return (
        <>
            <div
                className={`mm-termin mm-termin--${variante}`}
                // In der Liste gehört der Tipp der Karte, nicht der Zeile darunter.
                onClick={(ev) => ev.stopPropagation()}
                onPointerDown={(ev) => ev.stopPropagation()}
            >
                <span className="mm-termin-icon" aria-hidden>
                    <CalendarLtr20Regular />
                </span>
                <span className="mm-termin-text">
                    {zeit && <span className="mm-termin-zeit">{zeit}</span>}
                    {ort && <span className="mm-termin-ort">{ort}</span>}
                    {variante === "karte" && anzahlKonflikte !== null && (
                        <span className={`mm-termin-konflikt${anzahlKonflikte > 0 ? " mm-termin-konflikt--ja" : ""}`}>
                            {konfliktText(anzahlKonflikte)}
                        </span>
                    )}
                </span>
                {kann ? (
                    <button type="button" className="mm-termin-knopf" onClick={() => setOffen(true)}>
                        {stand ?? "Antworten"}
                    </button>
                ) : (
                    stand && <span className="mm-termin-stand">{stand}</span>
                )}
            </div>
            {/*
             * Das Sheet MUSS an den body: In der Liste steckt die Karte in der
             * Wisch-Zeile (`transform: translateX`), und ein transformierter
             * Vorfahr macht `position: fixed` zu „relativ zur Zeile" — das Sheet
             * klebte dann abgeschnitten als Kasten über der Mailliste.
             * Der Wrapper hält Klicks/Wischgesten aus der Zeile heraus (React
             * reicht Ereignisse aus Portalen an die Vorfahren im Baum weiter).
             */}
            {offen &&
                createPortal(
                    <div
                        className="mm-termin-portal"
                        onClick={(ev) => ev.stopPropagation()}
                        onPointerDown={(ev) => ev.stopPropagation()}
                        onPointerMove={(ev) => ev.stopPropagation()}
                        onPointerUp={(ev) => ev.stopPropagation()}
                    >
                <RsvpSheet
                    mailbox={mailbox}
                    messageId={messageId}
                    einladung={e}
                    tag={tag ?? null}
                    onSchliessen={() => setOffen(false)}
                    onGeantwortet={async (neu) => {
                        cache.set(`${mailbox.toLowerCase()}|${messageId}`, Promise.resolve({ einladung: neu, darfAntworten: true }));
                        setDaten({ einladung: neu, darfAntworten: true });
                        await onBeantwortet?.();
                    }}
                />
                    </div>,
                    document.body,
                )}
        </>
    );
};

const ANTWORTEN: { a: EinladungsAntwort; label: string; icon: React.ReactNode; klasse: string }[] = [
    { a: "zusagen", label: "Ja", icon: <CheckmarkCircle20Filled />, klasse: "ja" },
    { a: "vorbehalt", label: "Vielleicht", icon: <QuestionCircle20Filled />, klasse: "vielleicht" },
    { a: "absagen", label: "Nein", icon: <DismissCircle20Filled />, klasse: "nein" },
];

const uhr = (dt: string) => toGraphDate(dt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

/** Das Antwort-Sheet (Outlook iOS „RSVP"). */
const RsvpSheet: React.FC<{
    mailbox: string;
    messageId: string;
    einladung: TerminEinladung;
    tag: KalenderTermin[] | null;
    onSchliessen: () => void;
    onGeantwortet: (neu: TerminEinladung) => Promise<void>;
}> = ({ mailbox, messageId, einladung: e, tag, onSchliessen, onGeantwortet }) => {
    const erwartet = e.antwortErwuenscht !== false;
    const [antwortmail, setAntwortmail] = React.useState(true);
    const [kommentar, setKommentar] = React.useState("");
    const [laeuft, setLaeuft] = React.useState<EinladungsAntwort | null>(null);
    const [fehler, setFehler] = React.useState<string | null>(null);
    const konfliktIds = new Set(tag ? konflikte(e, tag).map((t) => t.id) : []);
    const ausschnitt = tag ? tagesAusschnitt(e, tag) : null;
    const mitMail = erwartet && antwortmail;

    const antworten = async (a: EinladungsAntwort) => {
        setFehler(null);
        const text = mitMail ? kommentar.trim() : "";
        if (!mussSenden(a, e.status, text)) {
            onSchliessen();
            return;
        }
        setLaeuft(a);
        let neu: TerminEinladung;
        try {
            neu = (await graphMailService.beantworteEinladung(mailbox, messageId, a, text, mitMail ? undefined : false))
                .einladung;
        } catch (err: unknown) {
            setFehler(einladungsFehlerText(err));
            setLaeuft(null);
            return;
        }
        try {
            await onGeantwortet(neu);
            onSchliessen();
        } catch {
            setFehler("Die Antwort ist gesendet, die Einladung ließ sich aber nicht löschen.");
        } finally {
            setLaeuft(null);
        }
    };

    return (
        <div className="cal-rd">
            <BottomSheet open title={e.betreff || "Termin"} onClose={onSchliessen}>
                <div className="mm-rsvp-zeit">
                    {terminZeitText(e)}
                    {e.ort ? ` · ${e.ort}` : ""}
                </div>
                {ausschnitt && (
                    <div className="mm-rsvp-tag" aria-label="Ihr Kalender an diesem Tag">
                        {ausschnitt.length === 0 ? (
                            <div className="mm-rsvp-leer">Keine weiteren Termine um diese Zeit.</div>
                        ) : (
                            ausschnitt.map((t) => {
                                const selbst = t.id === e.eventId || t.subject === e.betreff;
                                const konflikt = konfliktIds.has(t.id);
                                return (
                                    <div
                                        key={t.id}
                                        className={`mm-rsvp-eintrag${selbst ? " mm-rsvp-eintrag--selbst" : ""}${konflikt ? " mm-rsvp-eintrag--konflikt" : ""}`}
                                    >
                                        <span className="mm-rsvp-uhr">
                                            {uhr(t.start.dateTime)}–{uhr(t.end.dateTime)}
                                        </span>
                                        <span className="mm-label">{t.subject || "Termin"}</span>
                                        {konflikt && <span className="mm-rsvp-marke">Konflikt</span>}
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
                {erwartet && (
                    <label className="mm-rsvp-schalter">
                        <span>E-Mail an Organisator</span>
                        <input
                            type="checkbox"
                            role="switch"
                            checked={antwortmail}
                            onChange={(ev) => setAntwortmail(ev.target.checked)}
                        />
                    </label>
                )}
                {mitMail && (
                    <textarea
                        className="mm-rsvp-nachricht"
                        value={kommentar}
                        placeholder="Nachricht hinzufügen (optional)"
                        aria-label="Nachricht an den Organisator"
                        rows={2}
                        onChange={(ev) => setKommentar(ev.target.value)}
                    />
                )}
                {!erwartet && (
                    <div className="mm-sheet-hinweis">
                        Der Organisator erwartet keine Antwort — die Wahl gilt nur für Ihren Kalender.
                    </div>
                )}
                <div className="mm-rsvp-knoepfe">
                    {ANTWORTEN.map(({ a, label, icon, klasse }) => (
                        <button
                            key={a}
                            type="button"
                            className={`mm-rsvp-knopf mm-rsvp-knopf--${klasse}`}
                            disabled={laeuft !== null}
                            aria-busy={laeuft === a || undefined}
                            onClick={() => void antworten(a)}
                        >
                            {icon}
                            <span>{laeuft === a ? "…" : label}</span>
                        </button>
                    ))}
                </div>
                {fehler && (
                    <div className="mm-sheet-hinweis" role="alert" style={{ color: "#a4262c" }}>
                        {fehler}
                    </div>
                )}
            </BottomSheet>
        </div>
    );
};
